import Phaser from 'phaser';
import type { MorseSymbol } from '@/utils/morse';
import { THEME } from '@/config/theme';
import { MORSE_SPACING, SCORING_ZONES } from '@/utils/timing';

export type NoteOutcome = 'pending' | 'resolved';

export interface MorseNoteConfig {
  symbol: MorseSymbol;
  charIndex: number;
  symbolIndex: number;
  isLastInChar: boolean;
  expectedChar: string;
  spawnX: number;
  speed: number;
  hitZoneX: number;
  trackY: number;
}

export class MorseNoteSprite extends Phaser.GameObjects.Container {
  public symbol: MorseSymbol;
  public charIndex: number;
  public symbolIndex: number;
  public isLastInChar: boolean;
  public expectedChar: string;
  public outcome: NoteOutcome = 'pending';

  private speed: number;
  private hitZoneX: number;
  private sprite: Phaser.GameObjects.Rectangle;
  private glowEffect: Phaser.GameObjects.Rectangle | null = null;

  constructor(scene: Phaser.Scene, config: MorseNoteConfig) {
    super(scene, config.spawnX, config.trackY);

    this.symbol = config.symbol;
    this.charIndex = config.charIndex;
    this.symbolIndex = config.symbolIndex;
    this.isLastInChar = config.isLastInChar;
    this.expectedChar = config.expectedChar;
    this.speed = config.speed;
    this.hitZoneX = config.hitZoneX;

    this.sprite = this.createSprite(scene);
    this.add(this.sprite);

    scene.add.existing(this);
  }

  private createSprite(scene: Phaser.Scene): Phaser.GameObjects.Rectangle {
    const width = this.symbol === '.' ? MORSE_SPACING.dotWidth : MORSE_SPACING.dashWidth;
    const rect = scene.add.rectangle(0, 0, width, MORSE_SPACING.noteHeight, THEME.colors.primaryHex);
    rect.setOrigin(0, 0.5);
    rect.setStrokeStyle(2, THEME.colors.primaryDarkHex);
    return rect;
  }

  private getWidth(): number {
    return this.symbol === '.' ? MORSE_SPACING.dotWidth : MORSE_SPACING.dashWidth;
  }

  update(_time: number, delta: number): void {
    if (this.outcome === 'resolved') {
      return;
    }

    this.x -= this.speed * (delta / 1000);
    this.updateVisual();
  }

  getSpeedPxPerSec(): number {
    return this.speed;
  }

  setSpeed(speed: number): void {
    this.speed = speed;
  }

  private updateVisual(): void {
    const centerOffset = this.getCenterOffset(this.hitZoneX);

    // Add glow when approaching hit zone
    if (Math.abs(centerOffset) < SCORING_ZONES.ok * 1.5 && !this.glowEffect) {
      this.glowEffect = this.scene.add.rectangle(0, 0, this.getWidth(), MORSE_SPACING.noteHeight, THEME.colors.primaryHex, 0.3);
      this.glowEffect.setOrigin(0, 0.5);
      this.addAt(this.glowEffect, 0);
    }

    // Fade as note passes through hit zone
    // Fully opaque until center passes the hit zone; then fade over one ok-width
    if (centerOffset < 0) {
      const fadeRange = SCORING_ZONES.ok;
      const alpha = Math.max(0, Math.min(1, (centerOffset + fadeRange) / fadeRange));
      this.setAlpha(alpha);
    }
  }

  /**
   * Get center offset from hit zone (negative = past, positive = approaching)
   */
  getCenterOffset(hitZoneX: number): number {
    return (this.x + this.getWidth() / 2) - hitZoneX;
  }

  /**
   * Get start offset (left edge) from hit zone center
   */
  getStartOffset(hitZoneX: number): number {
    return this.x - hitZoneX;
  }

  /**
   * Get end offset (right edge) from hit zone center
   */
  getEndOffset(hitZoneX: number): number {
    return (this.x + this.getWidth()) - hitZoneX;
  }

  /**
   * Mark note as successfully hit - plays hit animation
   */
  resolveAsHit(): void {
    this.outcome = 'resolved';
    this.sprite.setFillStyle(THEME.colors.cyanHex);

    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      y: this.y - 30,
      duration: 200,
      onComplete: () => this.destroy(),
    });
  }

  /**
   * Mark note as passed unhit - fades out
   */
  resolveAsUnhit(): void {
    this.outcome = 'resolved';
    this.sprite.setFillStyle(THEME.colors.borderHex);
    this.sprite.setStrokeStyle(2, THEME.colors.backgroundPanelHex);

    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      duration: 150,
      onComplete: () => this.destroy(),
    });
  }

  /**
   * Check if note is off-screen and should be cleaned up
   */
  isOffScreen(): boolean {
    return this.x < -this.getWidth();
  }
}
